"use client"

import { useEffect, useState } from "react"

interface Partner {
  id: string
  name: string
  logo: string
  url: string
  description: string
  status: 'active' | 'inactive'
  created_at: string
  updated_at: string
}

export function PartnersSection() {
  const [partners, setPartners] = useState<Partner[]>([])
  const [loading, setLoading] = useState(true)

  const fetchPartners = async () => {
    try {
      const response = await fetch('/api/partners')
      if (response.ok) {
        const data = await response.json()
        setPartners(data.filter((partner: Partner) => partner.status === 'active'))
      }
    } catch (error) {
      console.error('Error fetching partners:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPartners()
  }, [])

  const heading = (
    <div className="max-w-xl">
      <span className="home-rule" aria-hidden />
      <h2
        id="partners"
        className="mt-6 font-heading text-[clamp(2.1rem,4.4vw,3.4rem)] font-medium leading-[1.05] tracking-[-0.03em] text-[var(--home-navy)]"
      >
        Our partners
      </h2>
      <p className="home-muted mt-5 max-w-xl text-[0.95rem] leading-relaxed md:text-lg">
        {loading
          ? "Loading our partners..."
          : "We collaborate with world-class organizations and institutions to deliver transformative experiences and opportunities."}
      </p>
    </div>
  )

  if (loading) {
    return (
      <section className="relative py-24 md:py-32" aria-labelledby="partners">
        <div className="home-wrap">{heading}</div>
      </section>
    )
  }

  if (partners.length === 0) {
    return null
  }

  return (
    <section className="relative py-24 md:py-32" aria-labelledby="partners">
      <div className="home-wrap">
        {heading}

        <ul className="mt-16 grid gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {partners.map((partner, idx) => (
            <li key={partner.id || idx}>
              <a
                href={`/api/redirect/partner/${partner.id}`}
                target="_blank"
                rel="noopener noreferrer"
                tabIndex={0}
                aria-label={`Visit ${partner.name}`}
                className="group flex h-full flex-col items-center justify-center gap-6 border border-[color-mix(in_oklch,var(--home-navy)_14%,transparent)] bg-[color-mix(in_oklch,var(--home-paper-2)_55%,transparent)] p-10 text-center transition-colors duration-200 hover:border-[var(--home-gold)]"
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="h-14 w-auto max-w-[180px] object-contain transition-transform duration-300 group-hover:scale-[1.04]"
                  loading="lazy"
                />

                <div>
                  <div className="font-heading text-lg font-medium tracking-[-0.02em] text-[var(--home-navy)] transition-colors duration-200 group-hover:text-[var(--home-gold)]">
                    {partner.name}
                  </div>
                  {partner.description && (
                    <div className="home-muted mt-2 text-sm leading-relaxed">
                      {partner.description}
                    </div>
                  )}
                </div>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}